import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import PosLandingAddon from './PosLandingAddon';
import PosLandingTick from './PosLandingTick';
import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/currencyUtil';
import { spinnerRing } from '../utils/bookingUtils';
import { useAnimatedNumber } from '../hooks/useAnimatedNumber';
import { planPeriodLabel, planSavings } from '../utils/posLandingUtil';
import { POS_EXTRA_STAFF_MAX, POS_WAREHOUSE_ADDON_MAX } from '../constants/posConst';
import {
  LANDING_BRAND_NAME, LANDING_BRAND_SUFFIX, LANDING_HEADLINE, LANDING_SECTIONS,
} from '../constants/posLandingConst';

/**
 * "See our pricing": a toggle across the plans configured on /admin ->
 * Subscription Plans — the free trial first, then the paid plans — and one
 * card showing whichever is selected.
 *
 * Every figure on the card comes from the plan or from Admin Settings — the
 * price, the struck-through compare-at price, the saving, the inclusions, the
 * free staff accounts and the add-on prices.
 *
 * @param {object} props
 * @param {object[]} props.plans - toggle plans (trial first), in display order
 * @param {boolean} props.loading
 * @param {Error|null} props.loadError
 * @param {number} props.activeIndex
 * @param {(index: number) => void} props.onSelect
 * @param {string} props.trialLabel - e.g. '7 days'; '' hides the tagline
 * @param {{extraStaff: number, warehouse: number}} props.addons
 * @param {(addons: {extraStaff: number, warehouse: number}) => void} props.onAddonsChange
 * @param {{extraStaffPrice: number, warehousePrice: number}} props.prices
 * @param {number|null} props.bestPercent - "save up to N%"; null hides it
 * @param {() => void} props.onCheckout - the card's button
 * @returns {JSX.Element}
 */
/**
 * The card's button wording when the plan does not set its own.
 *
 * @param {object} plan
 * @returns {string}
 */
const defaultButtonLabel = (plan) => {
  if (plan.isCustom) return 'Schedule a strategy call';
  if (plan.isTrial) return 'Start free trial';
  return 'Subscribe';
};

function PosLandingPricing({
  plans, loading, loadError, activeIndex, onSelect, trialLabel, addons, onAddonsChange, prices, bestPercent, onCheckout,
}) {
  const { config } = useConfig();
  const buttonRefs = useRef([]);
  const [slider, setSlider] = useState({ width: 0, height: 0, x: 0, y: 0 });

  const plan = plans[activeIndex] || null;
  const shownPrice = useAnimatedNumber(plan && !plan.isCustom && !plan.isTrial ? plan.price : 0);
  const savings = plan ? planSavings(plan) : null;

  /** Lays the navy pill under the pressed toggle button. @returns {void} */
  const moveSlider = useCallback(() => {
    const button = buttonRefs.current[activeIndex];
    if (!button) return;
    setSlider({ width: button.offsetWidth, height: button.offsetHeight, x: button.offsetLeft, y: button.offsetTop });
  }, [activeIndex]);

  // Measured after layout, and again on resize: the toggle wraps onto two
  // rows on a phone, so the pill's position cannot be worked out from index.
  useLayoutEffect(() => {
    moveSlider();
    window.addEventListener('resize', moveSlider);
    // Web fonts arriving late change the buttons' widths.
    document.fonts?.ready?.then(moveSlider).catch(() => {});
    return () => window.removeEventListener('resize', moveSlider);
  }, [moveSlider, plans.length]);

  const freeStaff = plan?.freeStaff || 0;
  const totalStaff = freeStaff + addons.extraStaff;

  const renderCard = () => {
    if (loading) {
      return <div className="plan-state">{spinnerRing('Loading plans')}</div>;
    }
    if (loadError || !plan) {
      return (
        <p className="plan-state">
          {loadError ? 'Pricing could not be loaded. Please refresh the page.' : 'Plans are being finalised. Check back soon.'}
        </p>
      );
    }
    return (
      <>
        <div className="plan-top">
          <div>
            <h3 className="plan-name">{LANDING_BRAND_NAME} <span>{LANDING_BRAND_SUFFIX}</span></h3>
            <p>{LANDING_HEADLINE.join(' ')}</p>
          </div>
          {/* The trial card says "Free" in the price already; the tagline would repeat it. */}
          {trialLabel && !plan.isTrial && <span className="tagline">{trialLabel} free</span>}
        </div>
        <div className="amount">
          {plan.isCustom && <b className="money is-text">Schedule a Strategy Session</b>}
          {plan.isTrial && <b className="money">Free</b>}
          {!plan.isCustom && !plan.isTrial && (
            <b className="money">{formatMoney(shownPrice, config)}</b>
          )}
          {savings && <s className="money">{formatMoney(plan.compareAtPrice, config)}</s>}
          <span>{planPeriodLabel(plan)}</span>
        </div>
        <p className="sub">
          {plan.description}
          {savings && (
            <>
              {plan.description ? ' ' : ''}
              <em>You save {formatMoney(savings.amount, config)}.</em>
            </>
          )}
        </p>
        <ul>
          {plan.features.map((feature, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${feature}-${index}`}><PosLandingTick />{feature}</li>
          ))}
        </ul>
        {plan.allowAddons && (
          <>
            <PosLandingAddon
              label="Add staff accounts"
              itemName="staff account"
              qty={addons.extraStaff}
              max={POS_EXTRA_STAFF_MAX}
              price={prices.extraStaffPrice}
              unitLabel="/staff"
              onChange={(extraStaff) => onAddonsChange({ ...addons, extraStaff })}
            >
              <div className="addon-eq">
                <span className="eq-chip">{freeStaff} free</span>
                <span className="eq-op">+</span>
                <span className="eq-chip">{addons.extraStaff} add-on{addons.extraStaff === 1 ? '' : 's'}</span>
                <span className="eq-op">=</span>
                <span className="eq-chip eq-total">{totalStaff} staff account{totalStaff === 1 ? '' : 's'}</span>
                {plan.staffNote && <small>{plan.staffNote}</small>}
              </div>
            </PosLandingAddon>
            <PosLandingAddon
              label="Add Warehouse / Stock Room"
              itemName="warehouse"
              qty={addons.warehouse}
              max={POS_WAREHOUSE_ADDON_MAX}
              price={prices.warehousePrice}
              unitLabel="/month"
              onChange={(warehouse) => onAddonsChange({ ...addons, warehouse })}
            >
              <div className="addon-eq">
                <span className="eq-chip">Stock room</span>
                <span className="eq-op">→</span>
                <span className="eq-chip eq-total">Store shelf</span>
                <small>move reserve stock across in a tap</small>
              </div>
            </PosLandingAddon>
          </>
        )}
        <button type="button" className="btn btn-primary btn-lg" onClick={onCheckout}>
          {plan.buttonLabel || defaultButtonLabel(plan)}
        </button>
        {plan.finePrint && <p className="fine">{plan.finePrint}</p>}
      </>
    );
  };

  return (
    <section className="pricing" id={LANDING_SECTIONS.pricing}>
      <div className="wrap">
        <div className="pr-head">
          <h2>See our pricing</h2>
          <p>
            Different pricing options
            {bestPercent ? `, save up to ${bestPercent}% for longer term plans.` : '.'}
          </p>
        </div>

        {plans.length > 1 && (
          <div className="toggle-wrap">
            <div className="toggle">
              <div
                className="slider"
                style={{ width: slider.width, height: slider.height, transform: `translate(${slider.x}px,${slider.y}px)` }}
              />
              {plans.map((item, index) => {
                const saving = planSavings(item);
                return (
                  <button
                    key={item.id}
                    ref={(element) => { buttonRefs.current[index] = element; }}
                    type="button"
                    aria-pressed={index === activeIndex}
                    onClick={() => onSelect(index)}
                  >
                    {item.name}
                    {saving && <> <span className="save">&minus;{saving.percent}%</span></>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="plan">{renderCard()}</div>
      </div>
    </section>
  );
}

export default PosLandingPricing;
